import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { MortgageTag } from '@core/util/tags';
import { MatterDocumentType, MortgageMatterFile } from '@models/matter-file';
import { FileDownloadDispatch } from '@state/file-download/fileDownload.dispatch';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from 'src/app/private/sale/documents/uploaded/modal/modal.component';
import { DeleteDocumentModalComponent } from '../delete-document-modal/delete-document-modal.component';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { MortgageSupTypeStrings } from '@core/util/file';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	MergedUploadedMatterDocumentSelectors,
	VirtualizedMatterFile,
} from '@state/global/uploaded-documents/upload-documents';
import { Observable } from 'rxjs';
import { MatterMortgage } from '@models/mortgage';
import {
	UploadedFileEditModalComponent,
	UploadedFileEditModalDialogData,
} from 'src/app/private/sale/documents/uploaded/edit-modal/edit-modal.component';
import {
	EditCategoryModalComponent,
	EditCategoryModalData,
} from '../edit-category-modal/edit-category-modal.component';
import { FileDownloadType } from '@state/file-download/fileDownload.model';
import { Select } from '@ngxs/store';
import { openClose } from '@core/animations/animations';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

const KEBAB_ACTIONS = {
	UPLOAD: 'upload',
	EDIT: 'edit',
	CHANGE_CATEGORY: 'changeCategory',
	HISTORY: 'history',
	DOWNLOAD: 'download',
	DELETE: 'delete',
};

const DocumentsKebabOptions = [
	{
		value: KEBAB_ACTIONS.UPLOAD,
		icon: ICON.UPLOAD,
		classes: ['text-green'],
		name: 'Upload new',
	},
	{
		value: KEBAB_ACTIONS.EDIT,
		icon: ICON.EDIT,
		name: 'Edit name',
	},
	{
		value: KEBAB_ACTIONS.CHANGE_CATEGORY,
		icon: ICON.REPEAT,
		name: 'Change category',
	},
	{
		value: KEBAB_ACTIONS.DOWNLOAD,
		icon: ICON.DOWNLOAD,
		name: 'Download',
	},
	{
		value: KEBAB_ACTIONS.DELETE,
		classes: ['text-red'],
		icon: ICON.DELETE,
		name: 'Delete',
	},
];

@Component({
	selector: 'app-mortgage-documents',
	templateUrl: './mortgage-documents.component.html',
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MortgageDocumentsComponent {
	public mortgageSupTypeStrings = MortgageSupTypeStrings;
	public kebabOptions = DocumentsKebabOptions;
	public mortgageDocumentType = MatterDocumentType.MORTGAGE;
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MergedUploadedMatterDocumentSelectors.getUploadedMortgageDocs)
	documents$: Observable<VirtualizedMatterFile[]>;
	@ViewSelectSnapshot(MatterMortgagesState.getSelectedMortgage)
	mortgage: MatterMortgage;

	drawerService = inject(DrawerService);

	constructor(
		private dialogRef: MatDialog,
		private downloadFileDispatch: FileDownloadDispatch,
	) {}

	openDocument(doc): void {
		const data: PdfModalData = {
			googleSrcUrl: doc.url,
			fileName: doc.name,
		};

		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: doc.name,
			noStyle: true,
		});
	}

	openUploadModal(): void {
		const data: UploadModalDocumentData = {
			matterId: this.matterId,
			defaultFileType: this.mortgageDocumentType,
			lockFileType: true,
			systemTags: [MortgageTag(this.mortgage.id)],
			contextId: this.mortgage.id,
		};
		this.dialogRef.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	onKebabSelect(document: MortgageMatterFile, selectedOption: string): void {
		switch (selectedOption) {
			case KEBAB_ACTIONS.DELETE:
				this.dialogRef.open(DeleteDocumentModalComponent, {
					data: {
						id: document.id,
						title: document.name,
					},
					maxWidth: '550px',
					width: '95vw',
				});
				break;
			case KEBAB_ACTIONS.UPLOAD: {
				const uploadData: UploadModalDocumentData = {
					matterId: this.matterId,
					defaultFileType: this.mortgageDocumentType,
					lockFileType: true,
					systemTags: [MortgageTag(this.mortgage.id)],
					replacingDocument: document,
					subType: document.subType,
				};

				this.dialogRef.open(UploadDocumentModalComponent, {
					data: uploadData,
					maxWidth: '550px',
					width: '95vw',
				});
				break;
			}
			case KEBAB_ACTIONS.EDIT: {
				const editNameData: UploadedFileEditModalDialogData = {
					file: document,
				};
				this.dialogRef.open(UploadedFileEditModalComponent, {
					data: editNameData,
					width: '80vw',
					maxWidth: '550px',
				});
				break;
			}
			case KEBAB_ACTIONS.CHANGE_CATEGORY: {
				const changeCategoryData: EditCategoryModalData = {
					file: document,
				};

				this.dialogRef.open(EditCategoryModalComponent, {
					data: changeCategoryData,
					width: '80vw',
					maxWidth: '550px',
				});

				break;
			}
			case KEBAB_ACTIONS.DOWNLOAD: {
				this.downloadFileDispatch.DownloadFile({
					name: document.name,
					mime: document.fileType,
					data: document.url,
					dataType: FileDownloadType.GOOGLE_URL,
				});
				break;
			}
			default:
				break;
		}
	}

	onKebabClick(evt: Event): void {
		evt.stopImmediatePropagation();
	}
}
